import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GetQRResponse,
  GetWaSettingsResponse,
  LogoutResponse,
  MessageLog,
  WhatsAppContactDTO,
  data_ack,
  data_whatsapp_message,
} from "../../models/WhatsApp.model";
import { getQRCode, GetMessagesLog, getWaSettings, globalWhatsAppContacts, logoutWhatsApp, SendMessage } from "../thunks/WhatsAppThunks";

export interface whatsAppState {
  loading: boolean;
  loadingSend: boolean;
  error: string | null;
  errorSend: string | null;
  initialized: boolean;
  messageLogs: MessageLog[];
  GlobalWhatsAppContactsLoading?: boolean;
  GlobalWhatsAppContactsError?: string | null;
  GlobalWhatsAppContacts?: WhatsAppContactDTO[];
  GetWaSettingsLoading?: boolean;
  GetWaSettingsError?: string | null;
  GetWaSettingsResponse?: GetWaSettingsResponse;
  LogoutLoading?: boolean;
  LogoutError?: string | null;
  LogoutResponse?: LogoutResponse;
  GetQRLoading?: boolean;
  GetQRError?: string | null;
  GetQRResponse?: GetQRResponse;
}

const initialState: whatsAppState = {
  messageLogs: [],
  loading: false,
  loadingSend: false,
  error: null,
  errorSend: null,
  initialized: false,
  GlobalWhatsAppContactsLoading: false,
  GlobalWhatsAppContactsError: null,
  GlobalWhatsAppContacts: [],
};

const INVALID_DATE_MS = Number.MAX_SAFE_INTEGER;

const hashStringToPositiveInt = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }

  return Math.abs(hash) || Date.now();
};

const resolveRealtimeMessageId = (messageID: string): number => {
  const parsedMessageId = Number(messageID);
  if (Number.isFinite(parsedMessageId)) {
    return parsedMessageId;
  }

  const normalized = (messageID || "").trim();
  return normalized ? hashStringToPositiveInt(normalized) : Date.now();
};

const resolveRealtimeDirection = (directionValue: string): number => {
  const normalized = (directionValue || "").trim().toLowerCase();

  if (
    normalized === "1" ||
    normalized === "outgoing" ||
    normalized === "sent"
  ) {
    return 1;
  }

  if (
    normalized === "0" ||
    normalized === "2" ||
    normalized === "incoming" ||
    normalized === "received"
  ) {
    return 0;
  }

  return 0;
};

const resolveRealtimeMessageStatus = (
  direction: number,
  rawStatus?: string,
): number => {
  const parsedStatus = Number(rawStatus);
  if (Number.isFinite(parsedStatus)) {
    return parsedStatus;
  }

  return direction === 1 ? 0 : 2;
};

const resolveMessageDateMs = (value: string): number => {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? INVALID_DATE_MS : parsed;
};

const compareMessageLogsByDate = (a: MessageLog, b: MessageLog): number => {
  const byDate =
    resolveMessageDateMs(a.datetime) - resolveMessageDateMs(b.datetime);
  if (byDate !== 0) {
    return byDate;
  }

  // Keep stable insertion order for identical timestamps.
  return 0;
};

const upsertMessageLogSorted = (
  messageLogs: MessageLog[],
  message: MessageLog,
): MessageLog[] => {
  const existingIndex = messageLogs.findIndex(
    (log) => log.messageID === message.messageID,
  );

  if (existingIndex >= 0) {
    const next = [...messageLogs];
    next[existingIndex] = {
      ...next[existingIndex],
      ...message,
    };

    return next.sort(compareMessageLogsByDate);
  }

  return [...messageLogs, message].sort(compareMessageLogsByDate);
};

const WhatsAppSlice = createSlice({
  name: "whatsApp",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSendError: (state) => {
      state.errorSend = null;
    },
    clearMessageLogs: (state) => {
      state.messageLogs = [];
      state.initialized = false;
      state.error = null;
      state.errorSend = null;
    },
    addIncomingWhatsAppMessage: (
      state,
      action: PayloadAction<data_whatsapp_message>,
    ) => {
      const payload = action.payload;
      const messageId = resolveRealtimeMessageId(payload.messageID);
      const direction = resolveRealtimeDirection(payload.direction);
      const parsedDate = new Date(payload.datetime);
      const timestamp = Number.isNaN(parsedDate.getTime())
        ? new Date().toISOString()
        : parsedDate.toISOString();

      const mappedMessage: MessageLog = {
        channel: "whatsapp",
        direction,
        datetime: timestamp,
        messageID: messageId,
        sender: payload.sender,
        receiver: payload.receiver,
        messageType: payload.messageType,
        messageBody: payload.messageBody,
        messageStatus: resolveRealtimeMessageStatus(
          direction,
          payload.messageStatus,
        ),
        fileURL: payload.fileURL,
        fileMimeType: payload.fileMimeType,
        fileName: payload.fileName,
        userName: payload.userName || payload.sender,
      };

      state.messageLogs = upsertMessageLogSorted(
        state.messageLogs,
        mappedMessage,
      );
      state.initialized = true;
    },
    applyWhatsAppAck: (state, action: PayloadAction<data_ack>) => {
      const { custom_data1, custom_data2 } = action.payload;
      const messageId = Number(custom_data1);

      if (!Number.isFinite(messageId)) {
        return;
      }

      const normalizedStatus = custom_data2?.toLowerCase() || "";
      const statusByText: Record<string, number> = {
        sent: 0,
        delivered: 1,
        read: 2,
      };
      const numericStatus = Number(custom_data2);
      const resolvedStatus = Number.isFinite(numericStatus)
        ? numericStatus
        : statusByText[normalizedStatus];

      const message = state.messageLogs.find(
        (item) => item.messageID === messageId,
      );

      if (!message) {
        return;
      }

      if (Number.isFinite(resolvedStatus)) {
        message.messageStatus = resolvedStatus;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(SendMessage.pending, (state) => {
        state.loadingSend = true;
        state.errorSend = null;
      })
      .addCase(SendMessage.fulfilled, (state, action) => {
        state.loadingSend = false;
        state.errorSend = null;

        const request = action.meta.arg;
        const response = action.payload;
        const parsedMessageId = Number(response?.idMessage);
        const fallbackMessageId = Date.now();
        const messageId = Number.isFinite(parsedMessageId)
          ? parsedMessageId
          : fallbackMessageId;

        const mappedMessage: MessageLog = {
          channel: "whatsapp",
          direction: 1,
          datetime: new Date().toISOString(),
          messageID: messageId,
          sender: "",
          receiver: request.phoneNumber || "",
          messageType: request.mediaType || "chat",
          messageBody: request.message || "",
          messageStatus: 0,
          fileURL: response?.mediaUrl || "",
          fileMimeType: request.media?.type || request.mediaType || "",
          fileName: request.media?.name || "",
          userName: "",
        };

        state.messageLogs = upsertMessageLogSorted(
          state.messageLogs,
          mappedMessage,
        );
        state.initialized = true;
      })
      .addCase(SendMessage.rejected, (state, action) => {
        state.loadingSend = false;
        state.errorSend = action.error.message || "Failed to send message";
      })
      .addCase(GetMessagesLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetMessagesLog.fulfilled, (state, action) => {
        state.loading = false;
        state.messageLogs = action.payload.json;
        state.initialized = true;
      })
      .addCase(GetMessagesLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch message logs";
      })
      .addCase(globalWhatsAppContacts.pending, (state) => {
        state.GlobalWhatsAppContactsLoading = true;
        state.GlobalWhatsAppContactsError = null;
      })
      .addCase(globalWhatsAppContacts.fulfilled, (state, action) => {
        state.GlobalWhatsAppContactsLoading = false;
        state.GlobalWhatsAppContacts = action.payload.contacts;
      })
      .addCase(globalWhatsAppContacts.rejected, (state, action) => {
        state.GlobalWhatsAppContactsLoading = false;
        state.GlobalWhatsAppContactsError =
          action.error.message || "Failed to fetch global WhatsApp contacts";
      })
      .addCase(getWaSettings.pending, (state) => {
        state.GetWaSettingsLoading = true;
        state.GetWaSettingsError = null;
      })
      .addCase(getWaSettings.fulfilled, (state, action) => {
        state.GetWaSettingsLoading = false;
        state.GetWaSettingsResponse = action.payload;
      })
      .addCase(getWaSettings.rejected, (state, action) => {
        state.GetWaSettingsLoading = false;
        state.GetWaSettingsError =
          action.error.message || "Failed to fetch WhatsApp settings";
      })
      .addCase(logoutWhatsApp.pending, (state) => {
        state.LogoutLoading = true;
        state.LogoutError = null;
      })
      .addCase(logoutWhatsApp.fulfilled, (state, action) => {
        state.LogoutLoading = false;
        state.LogoutResponse = action.payload;
        // Clear QR when logout is successful
        state.GetQRResponse = undefined;
        state.GetQRError = null;
      })
      .addCase(logoutWhatsApp.rejected, (state, action) => {
        state.LogoutLoading = false;
        state.LogoutError =
          action.error.message || "Failed to logout from WhatsApp";
      })
      .addCase(getQRCode.pending, (state) => {
        state.GetQRLoading = true;
        state.GetQRError = null;
      })
      .addCase(getQRCode.fulfilled, (state, action) => {
        state.GetQRLoading = false;
        state.GetQRResponse = action.payload;
      })
      .addCase(getQRCode.rejected, (state, action) => {
        state.GetQRLoading = false;
        state.GetQRError =
          action.error.message || "Failed to get QR code";
      });
  },
});

export const {
  clearError,
  clearSendError,
  clearMessageLogs,
  addIncomingWhatsAppMessage,
  applyWhatsAppAck,
} = WhatsAppSlice.actions;
export default WhatsAppSlice.reducer;
